// Lo que el programa NO puede calcular pero sí puede señalar.
//
// Hay datos que el motor no sabe interpretar y hoy resuelve callándose. El caso
// que destapó esto: un abono con fecha ANTERIOR al préstamo no cae en ningún
// periodo, así que el motor lo descarta —sin error, sin aviso— y el cliente
// aparece debiendo una plata que sí entregó.
//
// Medido en la cartera real el 2026-08-13: 14 abonos, seis clientes,
// $3.639.000 invisibles. A esa gente se le estaba cobrando de más.
//
// Por qué esto avisa en vez de arreglar solo: los casos NO son iguales. Uno con
// el abono un día antes es un error de dedo y pertenece a ese préstamo; otro con
// el abono cuatro meses antes es de un préstamo anterior que se renovó, y
// contarlo aquí sería perdonarle plata al cliente dos veces. El programa no
// tiene forma de distinguirlos. Lo que sí puede —y debe— es dejar de decidirlo
// en silencio y poner el problema donde alguien lo vea.

const unDia = 86400000;

// El día del calendario, como número entero. Las fechas guardadas se leen como
// medianoche UTC y Colombia va cinco horas atrás: comparar los instantes
// directamente movería el borde un día, que es justo lo que hay que medir bien.
function diaDe(f) {
  const d = f instanceof Date ? f : new Date(f);
  if (isNaN(d.getTime())) return null;
  return Math.floor(d.getTime() / unDia);
}

// Los abonos que el motor está descartando, y cuánta plata suman.
//
// Devuelve null cuando no hay nada que señalar, para que la pantalla pueda
// preguntar sin más: `if (aviso) …`.
export function abonosFueraDePlazo(prestamo, pagos = []) {
  if (!prestamo || !prestamo.start_date) return null;
  const inicio = diaDe(prestamo.start_date);
  if (inicio === null) return null;

  const hoy = diaDe(new Date());
  const antes = [];
  const futuros = [];

  for (const p of (Array.isArray(pagos) ? pagos : [])) {
    const d = diaDe(p.payment_date);
    if (d === null) continue;
    // El día del inicio SÍ cuenta: hay quien abona el mismo día que recibe.
    if (d < inicio) antes.push(p);
    else if (d > hoy) futuros.push(p);
  }

  if (!antes.length && !futuros.length) return null;

  const suma = (lista) => lista.reduce((t, p) => t + (Number(p.amount) || 0), 0);
  return {
    antes,
    futuros,
    // Solo los anteriores desaparecen de la cuenta. Los de fecha futura sí se
    // cuentan; se señalan porque casi siempre son un error de tecleo, y el día
    // que la fecha llegue el saldo cambiaría solo sin que nadie lo entienda.
    plataInvisible: suma(antes),
    plataFutura: suma(futuros),
  };
}

const plata = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

// El aviso, escrito para quien cobra: qué pasa, cuánto es y qué hacer.
export function textoDelAviso(aviso) {
  if (!aviso) return null;
  const partes = [];

  if (aviso.antes.length) {
    const n = aviso.antes.length;
    partes.push(
      `${n} ${n === 1 ? 'abono tiene' : 'abonos tienen'} fecha anterior al ` +
      `préstamo, así que ${n === 1 ? 'no se está contando' : 'no se están contando'}: ` +
      `${plata(aviso.plataInvisible)}. Revise las fechas.`);
  }
  if (aviso.futuros.length) {
    const n = aviso.futuros.length;
    partes.push(
      `${n} ${n === 1 ? 'abono está fechado' : 'abonos están fechados'} en el ` +
      `futuro (${plata(aviso.plataFutura)}).`);
  }
  return partes.join(' ');
}
